import { AssignmentStatusOptions } from 'constants/assignments';
import {
  chain,
  cloneDeep,
  every,
  filter,
  find,
  findIndex,
  flatten,
  forEach,
  get,
  groupBy,
  includes,
  indexOf,
  isEmpty,
  isNil,
  isNull,
  join,
  keyBy,
  map,
  orderBy,
  reduce,
  remove,
  size,
  some,
  sumBy
} from 'lodash';
import { randomNumber } from 'utils';

import { addStickerToImage } from 'pages/Teachers/shared/StickerAddingTool/utils';
import { uploadImage } from 'utils/files';
import { buildFilters } from 'utils/filters';
import { formatDate } from 'utils/moment';
import { notifyErrors, notifyInfo } from 'utils/notifications';

import {
  addStudentComment,
  bulkAddStudentComment,
  bulkUpdateTasksScores,
  bulkUpdateTasksStatuses,
  getStudentWorkList,
  updateTaskStatus,
  uploadAssignmentFeedback
} from '../sdk';

const emptyWork = ({ taskId, responseId, objectiveOrder }) => ({
  emptyWork: true,

  tracker_instance_objective_id: taskId,
  tracker_score_response_id: responseId,
  objective_order: objectiveOrder,
  status: 'assigned',
  id: randomNumber(),
  created_at: null,
  is_feedback: false,
  is_revision: false,
  original_filename: null,
  original_id: null,
  work_url: null,
  children: [],
  standards: []
});

export const fillEmptyStudentWork = (studentWorkList) => {
  // Adds work object representation of the tasks where there is no uploaded work

  const fullStudentWork = reduce(
    studentWorkList,
    (result, studentWork) => {
      const { student, work } = studentWork;
      const pages = filter(student.score_responses, ({ objective }) =>
        isNil(objective.parent)
      );

      const fullWork = map(orderBy(pages, 'objective.order'), (task) => {
        const existingWork = find(work, {
          tracker_score_response_id: task.id
        });

        if (!existingWork) {
          // There is no uploaded work for Task X
          return emptyWork({
            taskId: task.objective.id,
            responseId: task.id,
            objectiveOrder: task.objective.order
          });
        }

        return existingWork;
      });

      // Make sure the work order follows the task one
      return [
        ...result,
        { ...studentWork, work: orderBy(fullWork, 'objective_order') }
      ];
    },
    []
  );

  return fullStudentWork;
};

export const removeEmptyStudentWork = (studentWorkList) =>
  reduce(
    studentWorkList,
    (result, studentWork) => {
      const uploadedWork = filter(studentWork.work, (x) => !x.emptyWork);

      return [...result, { ...studentWork, work: uploadedWork }];
    },
    []
  );

export const filterStudentWorkByTaskIds = (studentWorkList, taskIds) =>
  reduce(
    studentWorkList,
    (result, studentWork) => {
      const filteredWork = filter(
        studentWork.work,
        ({ tracker_instance_objective_id }) =>
          includes(taskIds, tracker_instance_objective_id)
      );

      return [...result, { ...studentWork, work: filteredWork }];
    },
    []
  );

const orderStudentWorkByDate = (studentWorkList) => {
  // orderBy returns the null values at the top of the list when the order is descending.
  // We want to apply the ordering only to the non-null objects and return the null ones at the end.

  // remove mutates the given array. The removed elements are the return value of the function
  const studentWorkListCopy = cloneDeep(studentWorkList);
  const emptyWorkList = remove(studentWorkListCopy, ({ student }) =>
    isNull(student.last_submission_date)
  );

  const orderedWorkList = orderBy(
    studentWorkListCopy,
    ['student.is_teacher_demo_student', 'student.last_submission_date'],
    ['desc', 'desc']
  );

  return [...orderedWorkList, ...emptyWorkList];
};

export const getOrderedStudentWorkList = (studentWorkList, orderingValue) => {
  if (orderingValue === 'student.first_name') {
    return orderBy(
      studentWorkList,
      ['student.is_teacher_demo_student', 'student.first_name'],
      ['desc', 'asc']
    );
  }

  if (orderingValue === 'student.last_name') {
    return orderBy(
      studentWorkList,
      ['student.is_teacher_demo_student', 'student.last_name'],
      ['desc', 'asc']
    );
  }

  if (orderingValue === 'student.last_submission_date') {
    return orderStudentWorkByDate(studentWorkList);
  }

  return studentWorkList;
};

export const filterDemoStudentsWithEmptyWork = (studentWorkList) =>
  filter(studentWorkList, ({ student, work }) => {
    if (student.is_teacher_demo_student) {
      return some(work, (x) => !x.emptyWork);
    }
    return true;
  });

export const filterStudentWorkByStudentIds = (studentWorkList, studentIds) => {
  if (isEmpty(studentIds)) {
    return studentWorkList;
  }
  return filter(studentWorkList, (studentWork) =>
    includes(studentIds, studentWork.student.id)
  );
};

export const buildStudentsNamesList = (studentWorkList) =>
  orderBy(
    map(
      filter(
        studentWorkList,
        (studentWork) => !studentWork.student.is_teacher_demo_student
      ),
      (studentWork) => ({
        id: studentWork.student.id,
        name: studentWork.student.name
      })
    ),
    'name'
  );

export const buildSectionsOptions = (sections) =>
  map(sections, (section) => {
    const createdAtFormatted = formatDate(section.created_at);

    return {
      value: section.id,
      label: `${section.section_name} ${createdAtFormatted}`
    };
  });

export const addComment = ({ studentId, taskId, data }, { studentWork }) => {
  return addStudentComment(taskId, data).then(({ data: newComment }) => {
    const studentWorkCopy = cloneDeep(studentWork);

    // Find the student data in the studentWork array
    const studentIndex = findIndex(map(studentWorkCopy, 'student'), {
      id: studentId
    });
    const studentData = studentWorkCopy[studentIndex];

    // Find the current task in the student data
    const taskIndex = findIndex(studentData.student.score_responses, {
      id: taskId
    });
    const task = studentData.student.score_responses[taskIndex];

    // Append the new comment in the task comments
    task.comments = [...task.comments, newComment];
    // Mutate the arrays using the new objects
    studentData.student.score_responses[taskIndex] = task;
    studentWorkCopy[studentIndex] = studentData;

    return studentWorkCopy;
  });
};

export const addCommentBulk = (
  { text },
  { user, selectedResponses, studentWork }
) => {
  bulkAddStudentComment({
    tracker_score_responses: selectedResponses,
    text
  }).then(({ success, errors }) => {
    if (!success) {
      notifyErrors(errors);
    }
  });

  const newComment = () => ({
    text: text,
    id: randomNumber(),
    user: {
      id: user.id,
      name: user.name,
      is_student: false
    }
  });

  const updatedStudentWork = map(studentWork, (studentWork) => {
    studentWork.student.score_responses = map(
      studentWork.student.score_responses,
      (resp) => {
        if (includes(selectedResponses, resp.id)) {
          resp.comments = [...resp.comments, newComment()];
        }
        return resp;
      }
    );

    return studentWork;
  });

  return updatedStudentWork;
};

const uploadImageBlob = async (blob, activeWork) => {
  const feedbackFileId = await uploadImage({
    blob,
    name: 'feedback'
  });

  const data = await uploadAssignmentFeedback({
    feedback_file_id: feedbackFileId,
    original_work: activeWork.original_id || activeWork.id
  });

  return data;
};

export const addStickersBulk = (
  { stickerUrl },
  {
    selectedResponses,
    studentWork,
    updateWorkItemsInState,
    selectedWorksSelectedChildren
  }
) => {
  const selectedWorks = flatten(
    studentWork.map(({ work }) => {
      return work
        .map((workItem) => {
          if (
            indexOf(selectedResponses, workItem.tracker_score_response_id) !==
            -1
          ) {
            const activeChildIndex =
              selectedWorksSelectedChildren[workItem.tracker_score_response_id];
            if (
              !isEmpty(workItem.children) &&
              activeChildIndex <= workItem.children.length - 1
            ) {
              return workItem.children[activeChildIndex];
            }
            return workItem;
          }
          return undefined;
        })
        .filter((x) => !isNil(x));
    })
  ).filter((work) => !isEmpty(work) && work.work_url);

  updateWorkItemsInState(selectedWorks.map((x) => ({ ...x, isLoading: true })));

  Promise.all(
    selectedWorks.map((work) => {
      // Work_url can be empty if the selected box is empty
      if (work.work_url) {
        return addStickerToImage(work.work_url, stickerUrl).then((blob) => {
          return uploadImageBlob(blob, work);
        });
      }
      return undefined;
    })
  ).then((newWorkList) => {
    updateWorkItemsInState(newWorkList.filter((x) => !isNil(x)));
  });
};

export const updateWorkItems = ({ newWorkList }, { studentWork }) => {
  const newStudentWork = newWorkList.reduce((result, newWork) => {
    return result.map((studentWorkItem) => {
      const work = studentWorkItem.work.map((workItem) => {
        const newStatus = get(
          workItem,
          'children[0].status',
          AssignmentStatusOptions.ASSIGNED
        );
        if (workItem.id === newWork.original_id) {
          return {
            ...workItem,
            children: [{ ...newWork, status: newStatus }, ...workItem.children]
          };
        }
        if (isNil(newWork.original_id) && newWork.id === workItem.id) {
          return {
            ...workItem,
            children: [{ ...newWork, status: newStatus }, ...workItem.children]
          };
        }
        return workItem;
      });

      return { ...studentWorkItem, work };
    });
  }, cloneDeep(studentWork));

  return newStudentWork;
};

export const handleNewStudentWorkWebhookCallback = (
  { newStudentWorkIds },
  { studentWork }
) => {
  const getStudentWorkData = buildFilters({
    'student_work[]': newStudentWorkIds
  });

  return getStudentWorkList(getStudentWorkData).then(
    ({ data: studentWorkData, success }) => {
      if (!success) {
        return;
      }
      const studentWorkCopy = cloneDeep(studentWork);

      map(studentWorkData, ({ student, work }) => {
        // Find the student data in the studentWork array
        const studentIndex = findIndex(map(studentWorkCopy, 'student'), {
          id: student.id
        });

        if (studentIndex === -1) {
          return;
        }

        const studentData = studentWorkCopy[studentIndex];

        forEach(work, (workItem) => {
          const workOriginal = find(studentData.work, {
            tracker_instance_objective_id:
              workItem.tracker_instance_objective_id
          });

          if (isNil(workOriginal)) {
            return; // continue loop
          }

          if (workOriginal.emptyWork) {
            const emptyWorkIndex = findIndex(studentData.work, {
              id: workOriginal.id
            });

            workItem.children = [];
            studentData.work[emptyWorkIndex] = workItem;
          } else {
            workOriginal.status = workItem.status;
            workOriginal.children = [workItem, ...workOriginal.children];
          }
        });

        studentData.student.last_submission_date = student.last_submission_date;
        studentData.work = orderBy(studentData.work, 'objective_order');

        const objectiveNumbers = join(
          map(work, (sw) => sw.objective_order + 1),
          ', '
        );

        notifyInfo(`${student.name} submitted task ${objectiveNumbers}.`);
      });
      return studentWorkCopy;
    }
  );
};

export const changeTaskStatus = (
  { trackerScoreResponseId, studentId, status },
  { studentWork }
) => {
  return updateTaskStatus({
    trackerScoreResponseId,
    data: { status }
  }).then(({ success, errors }) => {
    if (success) {
      const studentWorkCopy = cloneDeep(studentWork);

      // Update status locally instead of refetching the data
      const studentWorkIndex = findIndex(map(studentWorkCopy, 'student'), {
        id: studentId
      });
      const activeTaskIndex = findIndex(
        get(studentWorkCopy, `[${studentWorkIndex}].work`),
        { tracker_score_response_id: trackerScoreResponseId }
      );

      const taskData = get(
        studentWorkCopy,
        `[${studentWorkIndex}].work[${activeTaskIndex}]`
      );
      if (taskData) {
        const children = map(taskData.children, (childWork) => ({
          ...childWork,
          status
        }));
        studentWorkCopy[studentWorkIndex].work[activeTaskIndex] = {
          ...taskData,
          status,
          children
        };

        return studentWorkCopy;
      }
    } else {
      notifyErrors(errors);
    }
  });
};

export const addSingleNewWork = (
  { studentId, activeTaskId, newWork },
  { studentWork }
) => {
  const studentWorkCopy = cloneDeep(studentWork);

  // Update status locally instead of refetching the data
  const studentWorkIndex = findIndex(map(studentWorkCopy, 'student'), {
    id: studentId
  });

  // studentWork[X].work is actually a list of tasks
  const taskDataIndex = findIndex(studentWorkCopy[studentWorkIndex].work, {
    id: activeTaskId
  });
  const taskData = studentWorkCopy[studentWorkIndex].work[taskDataIndex];

  const children = [newWork, ...taskData.children];

  studentWorkCopy[studentWorkIndex].work[taskDataIndex] = {
    ...taskData,
    children
  };

  return studentWorkCopy;
};

export const changeTasksStatusesBulk = (
  { status },
  { selectedResponses, selectedSectionId, studentWork }
) => {
  return bulkUpdateTasksStatuses(selectedSectionId, {
    tracker_score_responses: selectedResponses,
    status
  }).then(({ success, errors }) => {
    if (!success) {
      notifyErrors(errors);
      return;
    }

    const newStudentWork = map(studentWork, (studentWorkObj) => {
      studentWorkObj.work = map(studentWorkObj.work, (work) => {
        if (includes(selectedResponses, work.tracker_score_response_id)) {
          work.status = status;
          work.children = map(work.children, (childWork) => {
            childWork.status = status;
            return childWork;
          });
        }
        return work;
      });
      return studentWorkObj;
    });

    return newStudentWork;
  });
};

export const changeTasksScoresBulk = (
  { scoreValue },
  { selectedResponses, studentWork, trackerScoreResponses }
) => {
  const updateResponseValue = (resp) => {
    if (includes(selectedResponses, resp.id)) {
      resp.value = scoreValue;
      resp.raw_value = scoreValue;
    }
    return resp;
  };

  return bulkUpdateTasksScores({
    tracker_score_responses: selectedResponses,
    raw_value: scoreValue
  }).then(({ success, errors }) => {
    if (!success) {
      notifyErrors(errors);
      return;
    }

    const newStudentWork = map(studentWork, (sw) => {
      sw.student.score_responses = map(
        sw.student.score_responses,
        updateResponseValue
      );

      return sw;
    });

    const newTrackerScoreResponses = map(
      trackerScoreResponses,
      updateResponseValue
    );

    return { newStudentWork, newTrackerScoreResponses };
  });
};

const getFilteredStudentWorkList = (studentWork, filters) => {
  const visibleTaskIds = map(filters.tasks, 'id');
  const showAll = filters.show.value === 'all_students';
  const showAllStudentsAllTasks =
    filters.show.value === 'all_students_all_tasks';
  const showAllSubmittedAllTasks =
    filters.show.value === 'all_submitted_all_tasks';

  if ((showAll || showAllStudentsAllTasks) && isEmpty(visibleTaskIds)) {
    // Show all tasks for all students. Exclude the demo students with no submitted work
    return filterDemoStudentsWithEmptyWork(studentWork);
  }
  if ((showAll || showAllStudentsAllTasks) && !isEmpty(visibleTaskIds)) {
    // Show only the filtered tasks for all students,  Exclude the demo students with no submitted work
    const filteredWork = filterStudentWorkByTaskIds(
      studentWork,
      visibleTaskIds
    );
    return filterDemoStudentsWithEmptyWork(filteredWork);
  }

  const studentUploadedWork = removeEmptyStudentWork(studentWork);

  if ((!showAll || showAllSubmittedAllTasks) && isEmpty(visibleTaskIds)) {
    // Show all tasks for all uploaded student work (submissions)
    return studentUploadedWork;
  }
  if ((!showAll || showAllSubmittedAllTasks) && !isEmpty(visibleTaskIds)) {
    // Show only the filtered tasks for all uploaded student work (submissions)
    return filterStudentWorkByTaskIds(studentUploadedWork, visibleTaskIds);
  }
};

export const getVisibleStudentWorkList = ({
  studentWork,
  filters,
  ordering
}) => {
  const orderedStudentWork = getOrderedStudentWorkList(
    studentWork,
    ordering.value
  );
  const filteredStudentWork = getFilteredStudentWorkList(
    orderedStudentWork,
    filters
  );

  const visibleStudentIds = map(filters.students, 'id');
  return filterStudentWorkByStudentIds(
    filter(filteredStudentWork, ({ work }) => !isEmpty(work)),
    visibleStudentIds
  );
};

export const toggleStudentWorkItemSelect = ({
  studentWorkList,
  studentId,
  trackerScoreResponseId,
  selected
}) => {
  const studentWorkItemIndex = findIndex(
    studentWorkList,
    (x) => x.student.id === studentId
  );
  if (studentWorkItemIndex > -1) {
    studentWorkList[studentWorkItemIndex].selected = selected;

    if (trackerScoreResponseId) {
      const workItemIndex = findIndex(
        studentWorkList[studentWorkItemIndex].work,
        (x) => x.tracker_score_response_id === trackerScoreResponseId
      );
      if (workItemIndex > -1) {
        studentWorkList[studentWorkItemIndex].work[workItemIndex].selected =
          selected;
      }
    }
  }

  // TODO: Think how to remove the deep copy??
  return cloneDeep(studentWorkList);
};

export const toggleAllStudentWorkSelectValue = ({
  studentWorkList,
  selected
}) => {
  return map(studentWorkList, (swItem) => ({
    ...swItem,
    selected,
    work: map(swItem.work, (workItem) => ({
      ...workItem,
      selected
    }))
  }));
};

export const toggleAllStudentWorkSelect = ({ studentWorkList, showValue }) => {
  const selectedStudents = filter(studentWorkList, 'selected');

  if (size(selectedStudents) === size(studentWorkList)) {
    if (
      showValue === 'all_students_all_tasks' ||
      showValue === 'all_submitted_all_tasks'
    ) {
      // We want to make sure that all cards are selected in all modes
      const allWorkSelected = every(
        flatten(map(studentWorkList, 'work')),
        'selected'
      );
      return toggleAllStudentWorkSelectValue({
        studentWorkList: studentWorkList,
        selected: !allWorkSelected
      });
    }

    return toggleAllStudentWorkSelectValue({
      studentWorkList: studentWorkList,
      selected: false
    });
  }
  return toggleAllStudentWorkSelectValue({
    studentWorkList: studentWorkList,
    selected: true
  });
};

export const ensureStudentWorkSelect = ({
  studentWorkList,
  newStudentWorkList
}) => {
  return map(newStudentWorkList, (swItem) => {
    const currentSwItem = find(
      studentWorkList,
      (x) => x.student.id === swItem.student.id
    );
    swItem.selected = get(currentSwItem, 'selected', false);

    swItem.work = map(swItem.work, (workItem) => {
      const currentWorkItem = find(get(currentSwItem, 'work', []), {
        id: workItem.id
      });
      workItem.selected = get(currentWorkItem, 'selected', false);
      return workItem;
    });
    return swItem;
  });
};

export const setVisibleStudentWorkSelected = (
  swList,
  activeTasksIds,
  showFilter,
  includeEmpty
) => {
  // Removes all work items that are not visible to the user right now
  const studentWorkListWithOnlyActiveWork = map(swList, (swItem) => ({
    ...swItem,
    work: filter(swItem.work, (x) =>
      includes(activeTasksIds, x.tracker_score_response_id)
    )
  }));

  // Removes all empty work items
  const studentWorkListWithOnlyNonEmptyWork = includeEmpty
    ? studentWorkListWithOnlyActiveWork
    : filter(
        map(studentWorkListWithOnlyActiveWork, (swItem) => ({
          ...swItem,
          work: filter(swItem.work, (x) => !x.emptyWork)
        })),
        (sw) => !isEmpty(sw.work)
      );

  const selectedStudentWork = toggleAllStudentWorkSelect({
    studentWorkList: studentWorkListWithOnlyNonEmptyWork,
    showValue: showFilter
  });

  const currentStudentWorkIndex = reduce(
    swList,
    (result, swItem) => ({ ...result, [swItem.student.id]: swItem.work }),
    {}
  );

  const activeSelectedWorks = map(selectedStudentWork, (selectedSwItem) => {
    const currentSwItemWork =
      currentStudentWorkIndex[selectedSwItem.student.id];

    // Brings back the removed student work items from
    // `studentWorkListWithOnlyActiveWork` and `studentWorkListWithOnlyNonEmptyWork`
    const selectedSwItemWork = map(currentSwItemWork, (workItem) => {
      if (
        !includes(activeTasksIds, workItem.tracker_score_response_id) ||
        (!includeEmpty && workItem.emptyWork)
      ) {
        return workItem;
      }
      return find(selectedSwItem.work, {
        tracker_score_response_id: workItem.tracker_score_response_id
      });
    });
    return { ...selectedSwItem, work: selectedSwItemWork };
  });

  const selectedSwGroupedByStudent = groupBy(
    activeSelectedWorks,
    (sw) => sw.student.id
  );

  // Brings back empty work
  return map(swList, (swItem) => {
    return get(get(selectedSwGroupedByStudent, swItem.student.id), [0], swItem);
  });
};

export const getPercentageLabel = (results, value) => {
  // results is array of Objective entries e.g. key-value pair
  const total = sumBy(results, (result) => result[1]);
  const percentage = ((value / total) * 100).toFixed(0);

  return `${percentage}% (${value})`;
};

export const getScoreResponsesForStudents = (scoreResult, groupedStudents) =>
  chain(scoreResult.tracker_score_responses)
    .filter((scoreResponse) => get(groupedStudents, scoreResponse.student_id))
    .filter((scoreResponse) => scoreResponse.raw_value !== null)
    .value();

export const getScoreResponsesHistogram = (scoreResponses) =>
  chain(scoreResponses).groupBy('raw_value').mapValues('length').value();

export const getPresentStudents = (scoreResult, visibleStudentIds = []) => {
  const trackerScoreStudentIndex = keyBy(
    scoreResult.tracker_score_students,
    'student_id'
  );

  // If the student is not part from the index => there is no submitted student work
  const presentStudents = filter(
    scoreResult.students,
    (student) => !get(trackerScoreStudentIndex[student.id], 'is_excused', true)
  );

  return isEmpty(visibleStudentIds)
    ? presentStudents
    : filter(presentStudents, (student) =>
        includes(visibleStudentIds, student.id)
      );
};
